import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { SlideUpLine, FadeUp, Stagger, StaggerItem } from "@/pages/_shared/anim";

const services = [
  {
    code: "01",
    title: "Supply & Installation",
    desc: "Complete elevator supply and installation solutions for residential, commercial, healthcare, hospitality, and industrial requirements.",
    tags: ["Home", "Passenger", "Cargo", "Panoramic"],
  },
  {
    code: "02",
    title: "All-Brand Spare Parts",
    desc: "A comprehensive range of electrical, electronic, mechanical, control, safety, and escalator components across multiple brands.",
    tags: ["Electrical", "Electronic", "Mechanical", "Safety"],
  },
  {
    code: "03",
    title: "Technical Solutions",
    desc: "Professional troubleshooting and technical support for elevator and escalator systems.",
    tags: ["Diagnosis", "Support", "Programming"],
  },
  {
    code: "04",
    title: "PCB Repair",
    desc: "All-brand repair support for single-layer, double-layer, and multilayer PCBs.",
    tags: ["Single", "Double", "Multilayer"],
  },
  {
    code: "05",
    title: "Testing & Commissioning",
    desc: "Technical testing and commissioning support for elevator and escalator systems.",
    tags: ["Testing", "Commissioning", "Inspection"],
  },
  {
    code: "06",
    title: "Maintenance & Upgradation",
    desc: "Maintenance and modernization solutions designed to improve reliability, safety, and system performance.",
    tags: ["Maintenance", "Upgrade", "Reliability"],
  },
];

export default function Services() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (hovered) return;
    const id = setInterval(() => {
      setActiveIdx((i) => (i + 1) % services.length);
    }, 4000);
    return () => clearInterval(id);
  }, [hovered]);

  return (
    <section id="services" className="bg-crest-deep py-20 md:py-28" data-product-shop>
      <div className="px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <FadeUp>
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-crest-orange uppercase">
                <span className="w-6 h-px bg-crest-orange/60" /> Services & Capabilities
              </span>
            </FadeUp>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-tight mt-5">
              <SlideUpLine as="div">One Technical Partner.</SlideUpLine>
              <SlideUpLine as="div" delay={0.12} className="text-white">
                Complete Elevator Solutions.
              </SlideUpLine>
            </h2>
            <FadeUp delay={0.2}>
              <p className="text-white/70 text-base leading-relaxed mt-4 max-w-xl">
                Whether you need a replacement component, a technical diagnosis, system programming, maintenance support, or a complete elevator installation, Uplift One brings multiple capabilities together under one roof.
              </p>
            </FadeUp>
          </div>
        </div>

        <Stagger
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          stagger={0.08}
        >
          {services.map((s, i) => {
            const isActive = i === activeIdx;
            return (
              <StaggerItem key={s.code}>
                <motion.div
                  onMouseEnter={() => {
                    setHovered(true);
                    setActiveIdx(i);
                  }}
                  onMouseLeave={() => setHovered(false)}
                  animate={{
                    backgroundColor: isActive
                      ? "rgb(234, 117, 38)"
                      : "rgba(15, 27, 46, 0.3)",
                    borderColor: isActive
                      ? "rgb(234, 117, 38)"
                      : "rgba(255,255,255,0.1)",
                  }}
                  transition={{ duration: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
                  className="group border rounded-lg p-7 cursor-pointer h-full"
                >
                  <div className="flex items-start justify-between">
                    <motion.span
                      animate={{
                        color: isActive
                          ? "rgb(15, 27, 46)"
                          : "rgb(234, 117, 38)",
                      }}
                      transition={{ duration: 0.3 }}
                      className="font-display text-xs font-semibold tracking-widest"
                    >
                      {s.code}
                    </motion.span>
                    <motion.span
                      animate={{
                        borderColor: isActive
                          ? "rgba(15,27,46,0.4)"
                          : "rgba(255,255,255,0.2)",
                        color: isActive
                          ? "rgb(15, 27, 46)"
                          : "rgb(255,255,255)",
                      }}
                      transition={{ duration: 0.3 }}
                      className="w-9 h-9 flex items-center justify-center rounded-full border"
                    >
                      <i className="ri-arrow-right-up-line text-lg"></i>
                    </motion.span>
                  </div>
                  <motion.h3
                    animate={{
                      color: isActive
                        ? "rgb(15, 27, 46)"
                        : "rgb(255, 255, 255)",
                    }}
                    transition={{ duration: 0.3 }}
                    className="font-display text-2xl md:text-[26px] mt-12 leading-tight"
                  >
                    {s.title}
                  </motion.h3>
                  <motion.p
                    animate={{
                      color: isActive
                        ? "rgba(15,27,46,0.8)"
                        : "rgba(255,255,255,0.7)",
                    }}
                    transition={{ duration: 0.3 }}
                    className="text-sm mt-3 leading-relaxed"
                  >
                    {s.desc}
                  </motion.p>
                  <div className="flex flex-wrap gap-2 mt-5">
                    {s.tags.map((t) => (
                      <motion.span
                        key={t}
                        animate={{
                          borderColor: isActive
                            ? "rgba(15,27,46,0.3)"
                            : "rgba(255,255,255,0.15)",
                          color: isActive
                            ? "rgba(15,27,46,0.8)"
                            : "rgba(255,255,255,0.8)",
                        }}
                        transition={{ duration: 0.3 }}
                        className="text-xs px-3 py-1 rounded-full border whitespace-nowrap"
                      >
                        {t}
                      </motion.span>
                    ))}
                  </div>
                  <div className="mt-5 h-0.5 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      key={`${isActive}-${activeIdx}-${hovered}`}
                      initial={{ width: "0%" }}
                      animate={{ width: isActive && !hovered ? "100%" : "0%" }}
                      transition={{
                        duration: isActive && !hovered ? 4 : 0.3,
                        ease: "linear",
                      }}
                      className="h-full bg-crest-deep/40"
                    />
                  </div>
                </motion.div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </div>
    </section>
  );
}